import type { ChatState, ChatAction } from '../types';

export function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'SET_MESSAGES':
      return { ...state, messages: action.messages };
    case 'ADD_MESSAGE': {
      const index = state.messages.findIndex(
        message =>
          message.id === action.message.id ||
          (action.message.serverId &&
            message.sender === action.message.sender &&
            (message.id === action.message.serverId ||
              message.serverId === action.message.serverId))
      );
      if (index === -1) return { ...state, messages: [...state.messages, action.message] };
      const messages = [...state.messages];
      const previous = state.messages[index];
      messages[index] = {
        ...(previous.id !== action.message.id ? previous : {}),
        ...action.message,
        id: previous.id,
        timestamp: previous.timestamp,
      };
      return { ...state, messages };
    }
    case 'SET_INPUT':
      return { ...state, input: action.input };
    case 'SEND_START':
      return { ...state, loading: true, error: '', isTyping: true };
    case 'SEND_SUCCESS':
      return { ...state, loading: false, isTyping: false };
    case 'SEND_ERROR':
      return { ...state, loading: false, isTyping: false, error: action.error };
    case 'SET_TYPING':
      return { ...state, isTyping: action.isTyping };
    case 'SET_STREAMING':
      return { ...state, streamingMessage: action.message };
    case 'UPDATE_STREAMING':
      if (!state.streamingMessage) return state;
      if (action.requestId && state.streamingMessage.id !== action.requestId) return state;
      return { ...state, streamingMessage: { ...state.streamingMessage, text: action.text } };
    case 'ADD_TOOL_CALL':
      return { ...state, activeToolCalls: [...state.activeToolCalls, action.toolCall] };
    case 'UPDATE_TOOL_CALLS':
      return { ...state, activeToolCalls: action.toolCalls };
    case 'CLEAR_STREAMING':
      if (action.requestId && state.streamingMessage?.id !== action.requestId) return state;
      return { ...state, streamingMessage: null, activeToolCalls: [] };
    case 'CLEAR_ERROR':
      return { ...state, error: '' };
    case 'SET_ESCALATED':
      return { ...state, isEscalated: action.isEscalated };
    case 'MARK_MESSAGE_FAILED': {
      const failed = {
        ...action.message,
        status: 'failed' as const,
        error: action.error,
        ...(action.meta ? { meta: action.meta } : {}),
      };
      const exists = state.messages.some(m => m.id === action.message.id);
      return {
        ...state,
        messages: exists
          ? state.messages.map(m =>
              m.id === action.message.id ? { ...m, ...failed, timestamp: m.timestamp } : m
            )
          : [...state.messages, failed],
      };
    }
    case 'REMOVE_MESSAGE':
      return { ...state, messages: state.messages.filter(m => m.id !== action.id) };
    case 'SET_RECOVERY_ERROR':
      return {
        ...state,
        messages: state.messages.map(message =>
          message.id === action.id && message.status === 'failed' && message.meta?.outcomeUnknown
            ? { ...message, error: action.error }
            : message
        ),
      };
    case 'SET_MESSAGE_REACTION': {
      if (!state.messages.some(m => m.id === action.id)) return state;
      return {
        ...state,
        messages: state.messages.map(m => {
          if (m.id !== action.id) return m;
          const next: typeof m = { ...m, reaction: action.reaction };
          if (action.reaction === null) {
            delete next.feedbackComment;
          } else if (action.comment !== undefined) {
            next.feedbackComment = action.comment;
          }
          return next;
        }),
      };
    }
    default:
      return state;
  }
}

export const initialChatState: ChatState = {
  messages: [],
  input: '',
  loading: false,
  error: '',
  isTyping: false,
  isEscalated: false,
  streamingMessage: null,
  activeToolCalls: [],
};
